import { GEISINGER_AUTH_CONNECTION } from 'utils/auth-connection';
import {
  Box,
  Button,
  HeadingText,
  ParagraphText,
  TextAction,
  VisuallyHidden,
} from '@leagueplatform/genesis-core';
import { Link, useLocation } from 'react-router-dom';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { getAuthRedirectPath } from 'utils/auth-redirect-path';
import {
  ErrorState,
  LoadingIndicator,
} from '@leagueplatform/web-common-components';
import { useIntl } from '@leagueplatform/locales';
import { useLandingPage } from 'hooks/use-landing-page/use-landing-page.hook';

export const AuthRedirectPage = () => {
  const { formatMessage } = useIntl();
  const { hasError, showDevLoginOptions } = useLandingPage();
  const location = useLocation();

  if (hasError) {
    // Use standard copy, never expose provider error descriptions or retry
    // automatically after a cancelled/failed sign-in.
    return (
      <Box role="alert">
        <ErrorState error={null} hideButton />
      </Box>
    );
  }

  if (showDevLoginOptions) {
    const state = { redirectPath: getAuthRedirectPath(location.state) };
    return (
      <Box
        as="main"
        css={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '$two',
          background: '$surfaceBackgroundSecondary',
        }}
      >
        <Box
          as="section"
          aria-labelledby="dev-sign-in-title"
          css={{
            width: '100%',
            maxWidth: '48rem',
            padding: '$twoAndHalf',
            borderRadius: '$medium',
            background: '$surfaceCardSecondary',
            boxShadow: '$card',
            display: 'flex',
            flexDirection: 'column',
            gap: '$oneAndHalf',
          }}
        >
          <HeadingText id="dev-sign-in-title" level="1" size="lg">
            {formatMessage({ id: 'STR_AUTH_DEV_TITLE' })}
          </HeadingText>
          <ParagraphText>
            {formatMessage({ id: 'STR_AUTH_DEV_BODY' })}
          </ParagraphText>
          <Button
            as={Link}
            priority="primary"
            to={`${APP_MANAGED_ROUTES.SIGN_IN}?connection=${GEISINGER_AUTH_CONNECTION}`}
            state={state}
          >
            {formatMessage({ id: 'STR_AUTH_MEMBER_LINK' })}
          </Button>
          <Box
            css={{
              padding: '$oneAndHalf',
              borderRadius: '$small',
              background: '$surfaceBackgroundSecondary',
            }}
          >
            <ParagraphText css={{ marginBlockEnd: '$half' }}>
              {formatMessage({ id: 'STR_AUTH_DEV_OLD_LOGIN' })}
            </ParagraphText>
            <TextAction
              as={Link}
              to={`${APP_MANAGED_ROUTES.SIGN_IN}?login=dev`}
              state={state}
              css={{
                textDecoration: 'underline',
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: '2.4rem',
              }}
            >
              {formatMessage({ id: 'STR_AUTH_DEV_LINK' })}
            </TextAction>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      role="status"
      aria-label={formatMessage({ id: 'STR_AUTH_LOADING' })}
      aria-live="polite"
      css={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <VisuallyHidden>
        {formatMessage({ id: 'STR_AUTH_LOADING' })}
      </VisuallyHidden>
      <LoadingIndicator />
    </Box>
  );
};
